/**
 * Basemap choice store (Zustand): which background map sits under the
 * census blocks — street map, satellite imagery, or none.
 *
 * Persisted per browser in localStorage so a resident's preference sticks
 * across visits. MapShell applies the choice to the live map; the layer
 * toggles UI writes to this store.
 */
import { create } from 'zustand';

export type BasemapChoice = 'streets' | 'satellite' | 'none';

export const BASEMAP_CHOICES: BasemapChoice[] = ['streets', 'satellite', 'none'];

export const BASEMAP_LABELS: Record<BasemapChoice, string> = {
  streets: 'Streets',
  satellite: 'Satellite',
  none: 'None',
};

/** Shown as the control's tooltip so "None" reads as a choice, not a fault. */
export const BASEMAP_HINTS: Record<BasemapChoice, string> = {
  streets: 'Street map — roads, place names, and landmarks',
  satellite: 'Satellite imagery — aerial photography',
  none: 'No background map — census blocks only',
};

const STORAGE_KEY = 'rd:basemap-choice';

function readStored(): BasemapChoice {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'streets' || raw === 'satellite' || raw === 'none') return raw;
  } catch {
    // Storage unavailable — fall through to the default.
  }
  return 'streets';
}

interface BasemapState {
  choice: BasemapChoice;
  setChoice: (choice: BasemapChoice) => void;
}

export const useBasemapStore = create<BasemapState>()((set) => ({
  choice: readStored(),
  setChoice: (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // Non-fatal: the choice still applies for this session.
    }
    set({ choice });
  },
}));
